import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UsersRound } from "lucide-react";

import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  H3,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@lagoon/ui";

import { api, ApiError, type SiteGroup } from "../api.js";
import { Notice } from "./feedback.js";
import { Link } from "../router.js";

/**
 * The groups attached to this site.
 *
 * A group is curated once, on the Groups screen, and attached here. Membership
 * changes made there reach this site immediately, which is the reason to use a
 * group instead of retyping the same five addresses per site.
 */
export function SiteGroups(props: {
  siteId: string;
  navigate: (to: string) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState("");
  const [error, setError] = useState<string | null>(null);

  const attached = useQuery({
    queryKey: ["site-groups", props.siteId],
    queryFn: async () => (await api.listSiteGroups(props.siteId)).groups,
  });
  const mine = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.listGroups()).groups,
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["site-groups", props.siteId] });

  const attach = useMutation({
    mutationFn: (groupId: string) => api.attachGroup(props.siteId, groupId),
    onSuccess: async () => {
      setSelected("");
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Could not attach that group.");
    },
  });

  /**
   * Detaching, which has to say when it did not happen (LGN-128).
   *
   * A refused detach left the group attached and the row in place, which is
   * what success looks like too. Everyone in the group would still reach this
   * site, so the failure goes to the same notice the attach path uses.
   */
  const detach = useMutation({
    mutationFn: (groupId: string) => api.detachGroup(props.siteId, groupId),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not detach that group. Its people still have access.",
      );
    },
  });

  const attachedIds = new Set((attached.data ?? []).map((group) => group.id));
  const attachable = (mine.data ?? []).filter((group) => !attachedIds.has(group.id));

  const detaching = detach.isPending;
  const detachGroup = detach.mutate;
  const navigate = props.navigate;
  const columns = useMemo(
    () => siteGroupColumns(navigate, detachGroup, detaching),
    [navigate, detachGroup, detaching],
  );

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <div>
        <H3>Groups</H3>
        <Text size="sm" tone="muted" className="lg:mt-1">
          Attach a group to give everyone in it access. Curate the groups themselves on the{" "}
          <Link to="/groups" navigate={props.navigate}>
            Groups
          </Link>{" "}
          page; changes there apply here immediately.
        </Text>
      </div>

      {error === null ? null : <Notice tone="danger">{error}</Notice>}

      {attachable.length > 0 ? (
        <div className="lg:flex lg:flex-wrap lg:gap-3">
          <Select
            value={selected}
            onValueChange={(value) => {
              setSelected(value);
            }}
          >
            <SelectTrigger aria-label="Group to attach" className="lg:min-w-56 lg:flex-1">
              <SelectValue placeholder="Choose a group…" />
            </SelectTrigger>
            <SelectContent>
              {attachable.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name} ({group.memberCount} {group.memberCount === 1 ? "person" : "people"})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            disabled={attach.isPending || selected === ""}
            onClick={() => {
              attach.mutate(selected);
            }}
          >
            {attach.isPending ? "Attaching…" : "Attach"}
          </Button>
        </div>
      ) : null}

      <DataTable
        caption="Groups attached to this site"
        columns={columns}
        data={attached.data ?? []}
        loading={attached.isPending}
        loadingRows={2}
        empty={
          <EmptyState
            glow={false}
            icon={<UsersRound />}
            title="No groups attached"
            description={
              (mine.data ?? []).length === 0
                ? "Create one to reuse the same list of people across sites."
                : "Attach one above to give everybody in it access."
            }
            {...((mine.data ?? []).length === 0
              ? {
                  action: (
                    <Button
                      variant="outline"
                      onClick={() => {
                        props.navigate("/groups");
                      }}
                    >
                      Create a group
                    </Button>
                  ),
                }
              : {})}
          />
        }
      />
    </section>
  );
}

/**
 * The columns, at module scope.
 *
 * A `cell` renderer returns JSX and reads as a component to a linter, so the
 * definitions live outside the render path and take what they close over as
 * arguments.
 */
function siteGroupColumns(
  navigate: (to: string) => void,
  detachGroup: (groupId: string) => void,
  detaching: boolean,
): DataTableColumn<SiteGroup>[] {
  return [
    {
      accessorKey: "name",
      header: "Group",
      cell: (info) => (
        <Link to="/groups" navigate={navigate} className="lg:text-strong">
          {info.row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: "memberCount",
      header: "People",
      cell: (info) => (
        <span className="lg:font-mono lg:tabular-nums lg:text-muted-foreground">
          {info.row.original.memberCount}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <span className="lg:flex lg:justify-end">
          <Button
            variant="ghost"
            size="sm"
            disabled={detaching}
            onClick={() => {
              detachGroup(info.row.original.id);
            }}
          >
            Detach
          </Button>
        </span>
      ),
    },
  ];
}
